import { IDetailsRowProps } from '@fluentui/react';
import {
  DetailsList,
  EmptyMessage,
  IColumnWithDataType,
  IDetailsListProps,
  listCellStylesNoLeftRightPadding,
  ListRow,
  listRowStylesWidgetLine,
  Loader,
  loaderStylesSpinnerXLarge,
  useClassNames,
} from '@h2oai/ui-kit';
import { useHistory } from 'react-router-dom';

import { useQueryRecentlyUsedProjects } from '../../featurestore/featurestore.hooks';
import type { RecentlyUsedProject } from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { RoutePaths } from '../../pages/Routes';
import { formatDefaultDateAndTimeWithSeconds } from '../../utils/utils';
import {
  IRecentlyOpenedProjectListClassNames,
  IRecentlyOpenedProjectListStyles,
  recentlyOpenedProjectListStylesDefault,
} from './RecentlyOpenedProjectList.styles';

const columns: IColumnWithDataType[] = [
  {
    key: 'projectName',
    name: 'Project Name',
    fieldName: 'projectDescription',
    minWidth: 200,
    data: {
      headerFieldName: 'projectName',
      listCellProps: {
        emptyMessage: 'No Description',
        styles: listCellStylesNoLeftRightPadding,
      },
    },
  },
  {
    key: 'lastAccessAt',
    name: 'Last Access',
    fieldName: 'lastAccessAt',
    minWidth: 150,
    data: {
      listCellProps: {
        onRenderText: (item: RecentlyUsedProject) => (
          <>{formatDefaultDateAndTimeWithSeconds(item.lastAccessAt) || `-`}</>
        ),
        styles: listCellStylesNoLeftRightPadding,
      },
    },
  },
  {
    key: 'updatedAt',
    name: 'Updated On',
    fieldName: 'updatedAt',
    minWidth: 150,
    data: {
      listCellProps: {
        onRenderText: (item: RecentlyUsedProject) => <>{formatDefaultDateAndTimeWithSeconds(item.updatedAt) || `-`}</>,
        styles: listCellStylesNoLeftRightPadding,
      },
    },
  },
];

export const RecentlyOpenedProjectList = () => {
  const recentlyUsedProjectsQuery = useQueryRecentlyUsedProjects({
    meta: {
      toastError: (err: unknown) =>
        getErrorMessageBarMessage(err, `An error has occurred while loading the recently opened projects: `).message,
    },
  });
  const recentlyUsedProjects = recentlyUsedProjectsQuery.data?.projects;
  const history = useHistory();

  const detailsListProps: IDetailsListProps = {
    columns,
    isHeaderVisible: false,
    items: recentlyUsedProjects || [],
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [] } = props || {};
      return (
        <ListRow
          styles={listRowStylesWidgetLine}
          data={item}
          columns={columns}
          onClick={(e) => {
            history.push(`${RoutePaths.FEATURESTORE_PROJECT}/${item.projectId}?name=${item.projectName}`);
            e?.preventDefault();
          }}
        />
      );
    },
  };
  const classNames = useClassNames<IRecentlyOpenedProjectListStyles, IRecentlyOpenedProjectListClassNames>(
    'RecentlyOpenedProjectList',
    recentlyOpenedProjectListStylesDefault
  );
  return (
    <div className={classNames.root} data-test="recently-opened-project-list">
      {!recentlyUsedProjects ? (
        <Loader styles={loaderStylesSpinnerXLarge} label="Loading the recently opened projects..." />
      ) : recentlyUsedProjects.length ? (
        <div className={classNames.list} data-is-scrollable="true">
          <DetailsList {...detailsListProps} />
        </div>
      ) : (
        <EmptyMessage message="You don't have any recently opened projects" />
      )}
    </div>
  );
};
